import axios, { AxiosError } from "axios";
import { auth } from "../firebase";

export const API_BASE_URL = "https://api.ifaste.no";

type RequestMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

export class ApiRequestError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * Returns a valid Firebase ID token. Firebase refreshes it when it is about to expire,
 * so the panel keeps working after the first hour.
 */
export const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    sessionStorage.setItem("token", token);
    return token;
  }
  return sessionStorage.getItem("token");
};

export const errorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    const errors = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
    return errors || data?.message || data?.detail || error.message;
  }
  return error instanceof Error ? error.message : "Noe gikk galt.";
};

/**
 * Like useFetch, but throws ApiRequestError on failure so callers can show the backend's message.
 */
async function apiRequest<T>(
  url: string,
  method: RequestMethod,
  payload?: unknown,
  params?: Record<string, unknown>,
): Promise<T> {
  const token = await getAuthToken();
  try {
    const response = await axios({
      url: `${API_BASE_URL}${url}`,
      method,
      data: payload,
      params,
      headers: {
        "Api-key": process.env.REACT_APP_IFASTE_API_KEY || "",
        accept: "*/*",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return response.data as T;
  } catch (error) {
    const status = error instanceof AxiosError ? error.response?.status ?? 520 : 520;
    throw new ApiRequestError(status, errorMessage(error));
  }
}

export default apiRequest;
